
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function NeighborhoodGuidePage() {
  const [selectedCounty, setSelectedCounty] = useState('wayne');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('royal-oak');

  const counties = {
    'wayne': 'Wayne County',
    'oakland': 'Oakland County',
    'macomb': 'Macomb County'
  };

  const neighborhoods = {
    'wayne': [
      {
        id: 'plymouth',
        name: 'Plymouth',
        medianPrice: 365000,
        priceRange: '$285K - $485K',
        description: 'Historic charm meets modern convenience in this walkable downtown community.',
        highlights: ['Historic Downtown', 'Excellent Schools', 'Family-Friendly', 'Walkable'],
        demographics: { families: 68, professionals: 22, retirees: 10 },
        commute: { detroit: 25, airport: 15, downtown: 30 },
        image: 'https://public.readdy.ai/ai/img_res/34d361de1dd7dc082435cd33ecc944f4.jpg'
      },
      {
        id: 'livonia',
        name: 'Livonia',
        medianPrice: 285000,
        priceRange: '$225K - $385K',
        description: 'Established suburban community with excellent schools and family amenities.',
        highlights: ['Top Schools', 'Parks & Recreation', 'Affordable', 'Safe'],
        demographics: { families: 72, professionals: 18, retirees: 10 },
        commute: { detroit: 20, airport: 12, downtown: 25 },
        image: 'https://readdy.ai/api/search-image?query=Livonia%20Michigan%20suburban%20neighborhood%20with%20well-maintained%20homes%2C%20parks%2C%20family-friendly%20residential%20streets&width=600&height=400&seq=neighborhood-livonia&orientation=landscape'
      }
    ],
    'oakland': [
      {
        id: 'royal-oak',
        name: 'Royal Oak',
        medianPrice: 425000,
        priceRange: '$325K - $575K',
        description: 'Vibrant downtown with trendy restaurants, shops, and nightlife.',
        highlights: ['Downtown Scene', 'Walkable', 'Dining & Shopping', 'Young Professionals'],
        demographics: { families: 45, professionals: 45, retirees: 10 },
        commute: { detroit: 15, airport: 25, downtown: 20 },
        image: 'https://readdy.ai/api/search-image?query=Royal%20Oak%20Michigan%20downtown%20area%20with%20trendy%20restaurants%2C%20walkable%20streets%2C%20vibrant%20nightlife%20district&width=600&height=400&seq=neighborhood-royal-oak&orientation=landscape'
      },
      {
        id: 'rochester-hills',
        name: 'Rochester Hills',
        medianPrice: 485000,
        priceRange: '$385K - $685K',
        description: 'Upscale community with top-rated schools and luxury amenities.',
        highlights: ['Luxury Homes', 'Top Schools', 'Golf Courses', 'Upscale'],
        demographics: { families: 75, professionals: 20, retirees: 5 },
        commute: { detroit: 35, airport: 45, downtown: 40 },
        image: 'https://readdy.ai/api/search-image?query=Rochester%20Hills%20Michigan%20upscale%20residential%20neighborhood%20with%20luxury%20homes%2C%20well-maintained%20landscaping%2C%20prestigious%20community&width=600&height=400&seq=neighborhood-rochester&orientation=landscape'
      }
    ],
    'macomb': [
      {
        id: 'shelby-township',
        name: 'Shelby Township',
        medianPrice: 285000,
        priceRange: '$235K - $385K',
        description: 'Growing community with new developments and family-friendly amenities.',
        highlights: ['New Construction', 'Growing Community', 'Family-Friendly', 'Value'],
        demographics: { families: 78, professionals: 17, retirees: 5 },
        commute: { detroit: 30, airport: 35, downtown: 35 },
        image: 'https://readdy.ai/api/search-image?query=Shelby%20Township%20Michigan%20new%20residential%20development%20with%20modern%20homes%2C%20growing%20suburban%20community&width=600&height=400&seq=neighborhood-shelby&orientation=landscape'
      },
      {
        id: 'sterling-heights',
        name: 'Sterling Heights',
        medianPrice: 245000,
        priceRange: '$195K - $325K',
        description: 'Diverse community with excellent value and convenient location.',
        highlights: ['Diverse Community', 'Great Value', 'Convenient Location', 'Shopping'],
        demographics: { families: 70, professionals: 20, retirees: 10 },
        commute: { detroit: 25, airport: 30, downtown: 30 },
        image: 'https://readdy.ai/api/search-image?query=Sterling%20Heights%20Michigan%20diverse%20residential%20neighborhood%20with%20affordable%20homes%2C%20convenient%20shopping%20areas&width=600&height=400&seq=neighborhood-sterling&orientation=landscape'
      }
    ]
  };

  const currentNeighborhoods = neighborhoods[selectedCounty as keyof typeof neighborhoods];
  const currentNeighborhood = currentNeighborhoods.find(n => n.id === selectedNeighborhood) || currentNeighborhoods[0];

  const schoolRatings = {
    'plymouth': { elementary: 9, middle: 8, high: 9 },
    'livonia': { elementary: 9, middle: 9, high: 8 },
    'royal-oak': { elementary: 8, middle: 8, high: 8 },
    'rochester-hills': { elementary: 10, middle: 10, high: 10 },
    'shelby-township': { elementary: 8, middle: 7, high: 8 },
    'sterling-heights': { elementary: 7, middle: 7, high: 7 }
  };

  const amenities = {
    'plymouth': [
      { name: 'Plymouth Historical Museum', type: 'Culture', distance: '0.5 mi' },
      { name: 'Kellogg Park', type: 'Recreation', distance: '0.3 mi' },
      { name: 'Downtown Plymouth', type: 'Shopping', distance: '0.2 mi' },
      { name: 'Plymouth Community Center', type: 'Recreation', distance: '0.8 mi' }
    ],
    'livonia': [
      { name: 'Laurel Park Place', type: 'Shopping', distance: '2.1 mi' },
      { name: 'Rotary Park', type: 'Recreation', distance: '1.5 mi' },
      { name: 'Livonia Recreation Center', type: 'Recreation', distance: '1.8 mi' },
      { name: 'Greenmead Historical Park', type: 'Culture', distance: '2.3 mi' }
    ],
    'royal-oak': [
      { name: 'Detroit Zoo', type: 'Recreation', distance: '1.2 mi' },
      { name: 'Downtown Royal Oak', type: 'Entertainment', distance: '0.5 mi' },
      { name: 'Royal Oak Music Theatre', type: 'Entertainment', distance: '0.7 mi' },
      { name: 'Farmers Market', type: 'Shopping', distance: '0.4 mi' }
    ],
    'rochester-hills': [
      { name: 'Paint Creek Trail', type: 'Recreation', distance: '1.0 mi' },
      { name: 'Rochester Hills Museum', type: 'Culture', distance: '2.1 mi' },
      { name: 'Meadow Brook Theatre', type: 'Entertainment', distance: '3.2 mi' },
      { name: 'Great Lakes Crossing', type: 'Shopping', distance: '8.5 mi' }
    ],
    'shelby-township': [
      { name: 'Stony Creek Metropark', type: 'Recreation', distance: '3.2 mi' },
      { name: 'Lakeside Mall', type: 'Shopping', distance: '2.8 mi' },
      { name: 'Shelby Township Library', type: 'Culture', distance: '1.5 mi' },
      { name: 'River Bends Park', type: 'Recreation', distance: '2.1 mi' }
    ],
    'sterling-heights': [
      { name: 'Lakeside Mall', type: 'Shopping', distance: '1.8 mi' },
      { name: 'Sterling Heights Community Center', type: 'Recreation', distance: '2.1 mi' },
      { name: 'Dodge Park', type: 'Recreation', distance: '1.2 mi' },
      { name: 'Freedom Hill Amphitheatre', type: 'Entertainment', distance: '3.5 mi' }
    ]
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Metro Detroit Neighborhood Guide
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover the perfect community for your lifestyle. Explore neighborhoods across Wayne, Oakland, and Macomb Counties with detailed insights on schools, amenities, and market trends.
            </p>
          </div>

          {/* County Selector */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-black mb-4 text-center">Select County to Explore</h2>
            <div className="flex justify-center gap-4">
              {Object.entries(counties).map(([key, name]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedCounty(key);
                    setSelectedNeighborhood(neighborhoods[key as keyof typeof neighborhoods][0].id);
                  }}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selectedCounty === key
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-100 text-black hover:bg-gray-200'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Neighborhood Selector */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <h3 className="text-lg font-bold text-black mb-4">Featured Neighborhoods in {counties[selectedCounty as keyof typeof counties]}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {currentNeighborhoods.map((neighborhood) => (
                <button
                  key={neighborhood.id}
                  onClick={() => setSelectedNeighborhood(neighborhood.id)}
                  className={`p-4 rounded-lg border-2 transition-colors cursor-pointer text-left ${
                    selectedNeighborhood === neighborhood.id
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-200 hover:border-yellow-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-black">{neighborhood.name}</h4>
                    <span className="text-yellow-600 font-bold">${neighborhood.medianPrice.toLocaleString()}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{neighborhood.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {neighborhood.highlights.slice(0, 3).map((highlight, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detailed Neighborhood View */}
          <div className="bg-white rounded-lg overflow-hidden mb-8">
            <div className="relative">
              <img
                src={currentNeighborhood.image}
                alt={currentNeighborhood.name}
                className="w-full h-80 object-cover object-top"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                <div className="p-8 text-white">
                  <h2 className="text-4xl font-bold mb-2">{currentNeighborhood.name}</h2>
                  <p className="text-xl mb-4">{currentNeighborhood.description}</p>
                  <div className="flex items-center space-x-6">
                    <div>
                      <div className="text-2xl font-bold">${currentNeighborhood.medianPrice.toLocaleString()}</div>
                      <div className="text-sm opacity-90">Median Price</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{currentNeighborhood.priceRange}</div>
                      <div className="text-sm opacity-90">Price Range</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Demographics */}
                <div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    <i className="ri-group-line text-yellow-500 mr-2"></i>
                    Demographics
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Families</span>
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full"
                            style={{ width: `${currentNeighborhood.demographics.families}%` }}
                          ></div>
                        </div>
                        <span className="text-black font-medium">{currentNeighborhood.demographics.families}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Young Professionals</span>
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full"
                            style={{ width: `${currentNeighborhood.demographics.professionals}%` }}
                          ></div>
                        </div>
                        <span className="text-black font-medium">{currentNeighborhood.demographics.professionals}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Retirees</span>
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full"
                            style={{ width: `${currentNeighborhood.demographics.retirees}%` }}
                          ></div>
                        </div>
                        <span className="text-black font-medium">{currentNeighborhood.demographics.retirees}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Schools */}
                <div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    <i className="ri-school-line text-yellow-500 mr-2"></i>
                    School Ratings
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(schoolRatings[currentNeighborhood.id as keyof typeof schoolRatings] || {}).map(([level, rating]) => (
                      <div key={level} className="flex justify-between items-center">
                        <span className="text-gray-600 capitalize">{level}</span>
                        <div className="flex items-center">
                          <div className="flex space-x-1 mr-2">
                            {[...Array(10)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-2 h-2 rounded-full ${
                                  i < rating ? 'bg-yellow-500' : 'bg-gray-200'
                                }`}
                              ></div>
                            ))}
                          </div>
                          <span className="text-black font-medium">{rating}/10</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Commute Times */}
                <div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    <i className="ri-car-line text-yellow-500 mr-2"></i>
                    Commute Times
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Downtown Detroit</span>
                      <span className="text-black font-medium">{currentNeighborhood.commute.detroit} min</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">DTW Airport</span>
                      <span className="text-black font-medium">{currentNeighborhood.commute.airport} min</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Downtown Area</span>
                      <span className="text-black font-medium">{currentNeighborhood.commute.downtown} min</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Highlights */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-black mb-4">
                  <i className="ri-star-line text-yellow-500 mr-2"></i>
                  Neighborhood Highlights
                </h3>
                <div className="flex flex-wrap gap-2">
                  {currentNeighborhood.highlights.map((highlight, index) => (
                    <span key={index} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>

              {/* Nearby Amenities */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-black mb-4">
                  <i className="ri-map-pin-line text-yellow-500 mr-2"></i>
                  Nearby Amenities
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {amenities[currentNeighborhood.id as keyof typeof amenities]?.map((amenity, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-black">{amenity.name}</div>
                        <div className="text-sm text-gray-600">{amenity.type}</div>
                      </div>
                      <div className="text-sm text-gray-500">{amenity.distance}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-yellow-500 text-black rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Ready to Explore {currentNeighborhood.name}?</h3>
              <p className="text-black mb-6">
                Let me show you available properties and help you discover what makes this neighborhood special. Schedule a personalized tour today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Neighborhood Tour
                </button>
                <button className="bg-transparent border-2 border-black text-black px-8 py-3 rounded-lg hover:bg-black hover:text-white transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
